import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

// LocalStorage keys for caching -- scoped per-user (below) so switching
// accounts in the same browser (logout + a different login, or a stale tab
// surviving a token swap) can never restore one user's open workspace tabs
// into another user's session. Base names kept for clearAllUsersCache()'s
// prefix sweep.
const STORAGE_KEY_BASE = {
  OPEN_DASHBOARDS: 'waide_open_dashboards',
  DASHBOARDS_CACHE: 'waide_saved_dashboards_cache',
};

// Current user's id (falls back to email, matching the pattern already used
// in FlexStudio.js) -- never trust a value cached from a previous render,
// always re-read from AuthService so a mid-session user switch is honored.
const getUserScope = () => {
  const user = AuthService.getUser();
  return (user && (user.id || user.email)) || 'anonymous';
};

const scopedKey = (base) => `${base}_${getUserScope()}`;

class DashboardService {
  /**
   * List all user's dashboards (metadata only)
   * @returns {Promise<Array>} Array of dashboard metadata objects
   */
  static async listDashboards() {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      const data = await response.json();
      // Cache the list for quick access
      this.cacheDashboardList(data);
      return data;
    } catch (error) {
      console.error('Error listing dashboards:', error);
      // Return cached data if available -- a real last-known-good cache,
      // not fabricated data (see FH-97 for the AtomApp.js caller's own
      // separate fake-dashboard substitution, removed there).
      const cached = this.getCachedDashboardList();
      if (cached) return cached;
      throw error;
    }
  }

  /**
   * Get a full dashboard with all panel data
   * @param {string} dashboardId - The dashboard ID
   * @returns {Promise<Object>} Full dashboard object with panels
   */
  static async getDashboard(dashboardId) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        // Tagged the same way ChatService.js's handleResponse is (FH-93) --
        // reaching this point means the backend WAS reached and returned a
        // real, specific error, not a true network/transport failure.
        // Callers use this to show the real message instead of a generic one.
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting dashboard:', error);
      throw error;
    }
  }

  /**
   * Create a new dashboard
   * @param {string} name - Dashboard name
   * @returns {Promise<Object>} Created dashboard object
   */
  static async createDashboard(name) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name }),
      });
      if (!response.ok) {
        // Tagged the same way getDashboard/ChatService.js's handleResponse
        // are (FH-94/FH-93) -- reaching here means the backend WAS reached
        // and returned a real, specific error. Callers use this to show the
        // real message instead of faking success (FH-95).
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating dashboard:', error);
      throw error;
    }
  }

  /**
   * Update dashboard metadata (title, layout)
   * @param {string} dashboardId - The dashboard ID
   * @param {Object} updates - Updates to apply (title, layout)
   * @returns {Promise<Object>} Updated dashboard object
   */
  static async saveDashboard(dashboardId, updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(updates),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error saving dashboard:', error);
      throw error;
    }
  }

  /**
   * Delete a dashboard
   * @param {string} dashboardId - The dashboard ID
   * @returns {Promise<boolean>} Success status
   */
  static async deleteDashboard(dashboardId) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting dashboard:', error);
      throw error;
    }
  }

  /**
   * Add a panel to a dashboard
   * @param {string} dashboardId - The dashboard ID
   * @param {Object} panel - Panel data to add
   * @returns {Promise<Object>} Updated dashboard object
   */
  static async addPanel(dashboardId, panel) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}/panels`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(panel),
      });
      if (!response.ok) {
        // Tagged the same way getDashboard/ChatService.js's handleResponse
        // are (FH-94/FH-93) -- reaching here means the backend WAS reached
        // and returned a real, specific error. Callers use this to show the
        // real message instead of faking success (FH-95).
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding panel:', error);
      throw error;
    }
  }

  /**
   * Remove a panel from a dashboard
   * @param {string} dashboardId - The dashboard ID
   * @param {string} panelId - The panel ID to remove
   * @returns {Promise<boolean>} Success status
   */
  static async removePanel(dashboardId, panelId) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}/panels/${panelId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return true;
    } catch (error) {
      console.error('Error removing panel:', error);
      throw error;
    }
  }

  /**
   * Refresh a single panel's data
   * @param {string} dashboardId - The dashboard ID
   * @param {string} panelId - The panel ID to refresh
   * @returns {Promise<Object>} Updated panel data
   */
  static async refreshPanel(dashboardId, panelId) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}/panels/${panelId}/refresh`, {
        method: 'POST',
        headers: getHeaders(false),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error refreshing panel:', error);
      throw error;
    }
  }

  /**
   * Refresh all panels in a dashboard
   * @param {string} dashboardId - The dashboard ID
   * @returns {Promise<Object>} Updated dashboard with refreshed panels
   */
  static async refreshAllPanels(dashboardId) {
    try {
      const response = await fetch(`${API_BASE_URL}/dashboards/${dashboardId}/refresh`, {
        method: 'POST',
        headers: getHeaders(false),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const httpError = new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
        httpError.isBackendResponse = true;
        httpError.status = response.status;
        throw httpError;
      }
      return await response.json();
    } catch (error) {
      console.error('Error refreshing all panels:', error);
      throw error;
    }
  }

  // LocalStorage helpers for caching

  /**
   * Save open dashboards to localStorage
   * @param {Array} dashboards - Array of open dashboard objects
   */
  static saveOpenDashboards(dashboards) {
    try {
      localStorage.setItem(scopedKey(STORAGE_KEY_BASE.OPEN_DASHBOARDS), JSON.stringify(dashboards));
    } catch (error) {
      console.error('Error saving open dashboards to localStorage:', error);
    }
  }

  /**
   * Get open dashboards from localStorage
   * @returns {Array} Array of open dashboard objects
   */
  static getOpenDashboards() {
    try {
      const stored = localStorage.getItem(scopedKey(STORAGE_KEY_BASE.OPEN_DASHBOARDS));
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error getting open dashboards from localStorage:', error);
      return [];
    }
  }

  /**
   * Cache dashboard list for quick access in pin dialog
   * @param {Array} dashboards - Array of dashboard metadata
   */
  static cacheDashboardList(dashboards) {
    try {
      localStorage.setItem(scopedKey(STORAGE_KEY_BASE.DASHBOARDS_CACHE), JSON.stringify({
        data: dashboards,
        timestamp: Date.now(),
      }));
    } catch (error) {
      console.error('Error caching dashboard list:', error);
    }
  }

  /**
   * Get cached dashboard list
   * @returns {Array|null} Cached dashboard list or null if expired/missing
   */
  static getCachedDashboardList() {
    try {
      const stored = localStorage.getItem(scopedKey(STORAGE_KEY_BASE.DASHBOARDS_CACHE));
      if (!stored) return null;
      const { data, timestamp } = JSON.parse(stored);
      // Cache is valid for 5 minutes
      if (Date.now() - timestamp > 5 * 60 * 1000) {
        return null;
      }
      return data;
    } catch (error) {
      console.error('Error getting cached dashboard list:', error);
      return null;
    }
  }

  /**
   * Clear all dashboard caches -- called on logout. Sweeps every
   * open-dashboards / dashboards-cache key in this browser (any user
   * suffix), not just the current user's scoped one: catches leftover
   * unscoped legacy keys and any other user's leftover session from before
   * this fix, so a stale tab-restore can never resurface across accounts.
   */
  static clearCache() {
    try {
      localStorage.removeItem(scopedKey(STORAGE_KEY_BASE.OPEN_DASHBOARDS));
      localStorage.removeItem(scopedKey(STORAGE_KEY_BASE.DASHBOARDS_CACHE));
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith(STORAGE_KEY_BASE.OPEN_DASHBOARDS) || key.startsWith(STORAGE_KEY_BASE.DASHBOARDS_CACHE)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing dashboard cache:', error);
    }
  }
}

export default DashboardService;
